import type { AccountBalance } from "@ekidenfi/ts-sdk";

import { FAUCET_APT } from "@constants/tokens";
import { Address } from "@utils/Address";
import { extractApiErrorMessage } from "@utils/extractApiErrorMessage";

import { requestFunding } from "@src/api";
import { aptosClient } from "@src/api/aptos";
import { getEkidenSdkClient, isMockMode } from "@src/api/sdk";

export interface OnChainBalanceResult {
  account: string;
  balances: Map<string, string>;
}

export interface AccountBalanceResult {
  balances: AccountBalance[];
}

export class BalanceService {
  fetchOnChainBalance = async (account: string, quoteAssetMetadata: string): Promise<OnChainBalanceResult> => {
    if (isMockMode) {
      const tokenMap = new Map<string, string>();
      if (quoteAssetMetadata) {
        tokenMap.set(Address.from(quoteAssetMetadata).toString(), "50000000000");
      }
      tokenMap.set(Address.from(FAUCET_APT).toString(), "10000000000");

      return {
        account: Address.from(account).toString(),
        balances: tokenMap,
      };
    }

    const data = await aptosClient.getCurrentFungibleAssetBalances({
      options: { where: { owner_address: { _eq: account } } },
    });

    const tokenMap = new Map<string, string>();
    data.forEach(({ asset_type, amount }) => {
      if (!asset_type || !amount) return;
      tokenMap.set(Address.from(asset_type).toString(), String(amount));
    });

    return {
      account: Address.from(account).toString(),
      balances: tokenMap,
    };
  };

  fetchAccountBalance = async (): Promise<AccountBalanceResult> => {
    const response = await getEkidenSdkClient().account.getBalance();
    return { balances: response.list };
  };

  requestTestTokens = async (
    address: string,
    quoteAssetMetadata: string,
    onPending: () => number,
    onSuccess: (toastId: number) => void,
    onError: (message: string) => void,
  ): Promise<boolean> => {
    try {
      const tokens = quoteAssetMetadata ? [quoteAssetMetadata] : [];
      const amounts = quoteAssetMetadata ? [10000000] : [];

      const response = await requestFunding({
        receiver: address,
        tokens,
        amounts,
      });

      const toastId = onPending();

      await aptosClient.waitForTransaction({ transactionHash: response.txid, options: { checkSuccess: true } });

      setTimeout(() => {
        onSuccess(toastId);
      }, 1000);

      return true;
    } catch (error) {
      const message =
        extractApiErrorMessage(error) ?? "An error occurred while requesting test tokens. Please try again.";
      onError(message);
    }

    return false;
  };
}

export const balanceService = new BalanceService();
